import React, { useState, useMemo, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Checkbox,
  Upload,
  Modal,
  message,
} from "antd";
import type { UploadProps } from "antd/es/upload/interface";
import {
  InboxOutlined,
  FileTextOutlined,
  CustomerServiceOutlined,
  CheckCircleFilled,
  SafetyCertificateOutlined,
  CloseOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import Header from "../../components/Header";
import { uploadImage, submitFeedback, getFeedbackOptions } from "../../utils/api";
import "./index.scss";

function apiBody(res: any) {
  if (res?.parseObj && typeof res.parseObj === "object") return res.parseObj;
  return {};
}

function apiOk(res: any) {
  const mxfj = apiBody(res).mxfj ?? res?.mxfj;
  return mxfj === 200 || mxfj === "200";
}

function apiHds(res: any) {
  return apiBody(res).hds ?? res?.hds;
}

function apiFzgz(res: any) {
  const body = apiBody(res);
  const fzgz = body.fzgz;
  if (fzgz && typeof fzgz === "object") return fzgz;
  if (Array.isArray(fzgz)) return fzgz;
  return body;
}

const { TextArea } = Input;
const { Dragger } = Upload;

interface MotivoOption {
  code: number | string;
  name: string;
  uploadFileTitle?: string;
  needUpload?: boolean;
}

interface TipoOption {
  code: number | string;
  name: string;
  children: MotivoOption[];
}

const MAX_SIZE = 3 * 1024 * 1024;
const MAX_FILES = 5;
const ALLOW_EXT = ["pdf", "png", "jpg", "jpeg"];

interface UploadedItem {
  uid: string;
  name: string;
  ext: string;
  size: number;
  status: "uploading" | "done" | "failed";
  resourceId?: string;
  fileUrl?: string;
  filePath?: string;
}

const formatSize = (bytes: number) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const getExt = (name: string) => {
  const idx = String(name).lastIndexOf(".");
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : "";
};

const fileToBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

const QuejasyReclamos: React.FC = () => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [detalleLen, setDetalleLen] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [tipoOptionsList, setTipoOptionsList] = useState<TipoOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  const tipoValue = Form.useWatch("tipoPresentacion", form);
  const motivoValue = Form.useWatch("motivo", form);

  const motivoOptionsList = useMemo<MotivoOption[]>(() => {
    const selected = tipoOptionsList.find((o) => o.code === tipoValue);
    return selected?.children || [];
  }, [tipoOptionsList, tipoValue]);

  const selectedMotivo = useMemo<MotivoOption | undefined>(() => {
    return motivoOptionsList.find((o) => o.code === motivoValue);
  }, [motivoOptionsList, motivoValue]);

  const needUpload = useMemo(
    () => selectedMotivo?.needUpload === true,
    [selectedMotivo]
  );

  const uploadingCount = useMemo(
    () => uploadedFiles.filter((f) => f.status === "uploading").length,
    [uploadedFiles]
  );

  useEffect(() => {
    const loadFeedbackOptions = async () => {
      setOptionsLoading(true);
      try {
        const res: any = await getFeedbackOptions({
          mxwbosYjr: "FEEDBACK",
        });
        const fzgz = apiFzgz(res);
        const rawList = Array.isArray(fzgz) ? fzgz : [];
        if (!apiOk(res) && !rawList.length) {
          messageApi.error(
            apiHds(res) || "No se pudieron cargar las opciones"
          );
          return;
        }
        setTipoOptionsList(
          rawList.map((item: any) => ({
            code: item.id,
            name: item.nameEs,
            children: (item.subCategories || []).map((sub: any) => ({
              code: sub.subId,
              name: sub.nameEs,
              uploadFileTitle: sub.uploadFileTitle,
              needUpload: sub.needFile,
            })),
          }))
        );
      } catch (err) {
        console.error("[getFeedbackOptions] error:", err);
      } finally {
        setOptionsLoading(false);
      }
    };
    loadFeedbackOptions();
  }, [messageApi]);

  const updateFile = (uid: string, patch: Partial<UploadedItem>) => {
    setUploadedFiles((prev) =>
      prev.map((f) => (f.uid === uid ? { ...f, ...patch } : f))
    );
  };

  const beforeUpload = (file: File) => {
    if (uploadedFiles.length >= MAX_FILES) {
      messageApi.warning("Máximo 5 archivos adjuntos");
      return Upload.LIST_IGNORE;
    }
    const ext = getExt(file.name);
    if (!ALLOW_EXT.includes(ext)) {
      messageApi.error("Solo se permiten archivos PDF, PNG o JPG");
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_SIZE) {
      messageApi.warning("Cada archivo debe ser menor a 3MB");
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const customRequest: UploadProps["customRequest"] = async (options) => {
    const { file, onSuccess, onError } = options;
    const raw = file as File;
    const ext = getExt(raw.name);
    const uid = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    setUploadedFiles((prev) => [
      ...prev,
      {
        uid,
        name: raw.name,
        ext,
        size: raw.size,
        status: "uploading",
      },
    ]);

    try {
      const base64 = await fileToBase64(raw);
      const res: any = await uploadImage({
        ohzsjWzdj64: base64,
        cedowjddRrqj: "feedback",
      });
      const fzgz = apiFzgz(res) || {};
      if (apiOk(res)) {
        updateFile(uid, {
          status: "done",
          resourceId: fzgz.kjdxekmjAf,
          fileUrl: fzgz.boujVku,
          filePath: fzgz.boujBzgl,
        });
        onSuccess?.({}, raw as any);
      } else {
        updateFile(uid, { status: "failed" });
        messageApi.error(
          apiHds(res) || "No se pudo subir el archivo"
        );
        onError?.(new Error("upload failed"));
      }
    } catch (err) {
      console.error("[uploadImage] error:", err);
      updateFile(uid, { status: "failed" });
      messageApi.error("No se pudo subir el archivo");
      onError?.(err as Error);
    }
  };

  const removeFile = (uid: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.uid !== uid));
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
    } catch {
      messageApi.error("Complete los campos requeridos antes de enviar.");
      return;
    }

    const values = form.getFieldsValue();

    if (values.agree !== true) {
      messageApi.error("Marcá la opción correspondiente antes de realizar el envío.");
      return;
    }

    if (needUpload && !uploadedFiles.length) {
      messageApi.error("Adjuntá la documentación requerida para este motivo.");
      return;
    }

    if (uploadingCount > 0) {
      messageApi.warning("Espere a que terminen las subidas");
      return;
    }
    if (uploadedFiles.some((f) => f.status === "failed")) {
      messageApi.error("Algunos archivos no se subieron correctamente");
      return;
    }

    setSubmitting(true);
    try {
      const tipoOpt = tipoOptionsList.find((o) => o.code === values.tipoPresentacion);
      const motivoOpt = (tipoOpt?.children || []).find(
        (o) => o.code === values.motivo
      );
      const imgIds = uploadedFiles
        .map((f) => f.resourceId)
        .filter((id) => id !== undefined && id !== null && id !== "")
        .join(",");

      const payload = {
        edjkwzhj: (values.fullName || "").trim(),
        fxmehjwgAf: values.dni,
        qlxwj: (values.phone || "").trim(),
        jhzou: (values.email || "").trim(),
        edjkRrqj: 3,
        bjjfczmiRrqj: tipoOpt ? tipoOpt.code : 0,
        kjzdxw: motivoOpt ? String(motivoOpt.code) : "",
        fjgzou: (values.detalle || "").trim(),
        ohsAfd: imgIds,
        azwgSjdeug: (values.resultadoEsperado || "").trim(),
      };

      const res: any = await submitFeedback(payload);
      if (apiOk(res)) {
        setShowSuccess(true);
        form.resetFields();
        form.setFieldsValue({ agree: true });
        setDetalleLen(0);
        setUploadedFiles([]);
      } else {
        messageApi.error(
          apiHds(res) || "No se pudo enviar. Intente nuevamente."
        );
      }
    } catch (err) {
      console.error("[feedback] submit error:", err);
      messageApi.error("No se pudo enviar. Intente nuevamente.");
    } finally {
      setSubmitting(false);
    }
  };

  const backToForm = () => {
    setShowSuccess(false);
  };

  return (
    <div className="quejas-container">
      {contextHolder}
      <Header variant="dark" />

      <div className="hero">
        <div className="hero-inner">
          <span className="hero-badge">
            <CustomerServiceOutlined /> Atención al Usuario
          </span>
          <h1 className="hero-title">Quejas y Reclamos</h1>
          <p className="hero-desc">
            Si necesitás realizar una consulta, presentar un reclamo o gestionar una solicitud relacionada con nuestros servicios, podés hacerlo mediante este canal. Luego de enviar el formulario y finalizar la verificación correspondiente, recibirás un correo de confirmación.
          </p>
        </div>
      </div>

      <div className="form-wrap">
          <div className="form-card">
            <div className="form-card-head">
              <div className="head-icon">
                <FileTextOutlined />
              </div>
              <div className="head-text">
                <div className="head-title">Formulario de Presentación</div>
                <div className="head-sub">Completá los datos antes de enviar la solicitud.</div>
              </div>
            </div>

            <Form
              form={form}
              layout="vertical"
              requiredMark={(label, info) => (
                <>
                  {label} {info.required && <span className="required-mark">*</span>}
                </>
              )}
              // TODO: 测试提交完成后删除这些默认值
              initialValues={{
                fullName: "",
                dni: "",
                phone: "",
                email: "",
                tipoPresentacion: "",
                motivo: "",
                detalle: "",
                resultadoEsperado: "",
                agree: true,
              }}
              className="quejas-form"
              onValuesChange={(changed) => {
                if (changed.detalle !== undefined) {
                  setDetalleLen((changed.detalle || "").length);
                }
              }}
            >
              <Form.Item
                label="Nombre y apellido"
                name="fullName"
                rules={[{ required: true, message: "Ingrese nombre y apellido" }]}
              >
                <Input placeholder="Tu nombre completo" maxLength={50} allowClear />
              </Form.Item>

              <div className="form-row">
                <Form.Item
                  label="DNI"
                  name="dni"
                  rules={[
                    { required: true, message: "Ingrese el DNI" },
                    {
                      pattern: /^\d{8}$/,
                      message: "El DNI debe tener exactamente 8 dígitos",
                    },
                  ]}
                  normalize={(val: string) => String(val || "").replace(/\D/g, "").slice(0, 8)}
                >
                  <Input placeholder="8 dígitos" maxLength={8} allowClear />
                </Form.Item>

                <Form.Item
                  label="Teléfono"
                  name="phone"
                  rules={[{ required: true, message: "Ingrese el teléfono" }]}
                  normalize={(val: string) => String(val || "").replace(/[^\d+\s-]/g, "").slice(0, 20)}
                >
                  <Input placeholder="" allowClear />
                </Form.Item>
              </div>

              <Form.Item
                label="Correo electrónico"
                name="email"
                rules={[
                  { required: true, message: "Ingrese el correo electrónico" },
                  { type: "email", message: "Correo electrónico no válido" },
                ]}
              >
                <Input placeholder="usuario@ejemplo.com" allowClear />
              </Form.Item>

              <div className="form-row">
                <Form.Item
                  label="Tipo de presentación"
                  name="tipoPresentacion"
                  rules={[{ required: true, message: "Seleccione el tipo de presentación" }]}
                >
                  <Select
                    placeholder="Seleccionar tipo"
                    loading={optionsLoading}
                    options={tipoOptionsList.map((o) => ({ label: o.name, value: o.code }))}
                    onChange={() => form.setFieldValue("motivo", undefined)}
                  />
                </Form.Item>

                <Form.Item
                  label="Motivo"
                  name="motivo"
                  rules={[{ required: true, message: "Seleccione el motivo" }]}
                >
                  <Select
                    placeholder="Seleccionar motivo"
                    disabled={!tipoValue}
                    options={motivoOptionsList.map((o) => ({ label: o.name, value: o.code }))}
                  />
                </Form.Item>
              </div>

              <Form.Item
                label={
                  <div className="detalle-label">
                    <span>
                      Detalle del caso <span className="required-mark">*</span>
                    </span>
                    <span className={`detalle-counter ${detalleLen >= 2000 ? "is-limit" : ""}`}>
                      {detalleLen} / 2000
                    </span>
                  </div>
                }
                name="detalle"
                rules={[{ required: true, message: "Describa su caso" }]}
                className="detalle-item"
              >
                <TextArea
                  placeholder="Describa su caso con el mayor detalle posible"
                  rows={5}
                  maxLength={2000}
                />
              </Form.Item>

              <Form.Item label="Resultado esperado" name="resultadoEsperado">
                <Input placeholder="Ej.: devolución, revisión, cancelación" allowClear />
              </Form.Item>

              <Form.Item
                label={
                  <span>
                    Adjuntar documentación
                    {needUpload && <span className="required-mark"> *</span>}
                  </span>
                }
                className="upload-item"
              >
                {needUpload && selectedMotivo && (
                  <div className="upload-required-tip">
                    {selectedMotivo.name}
                  </div>
                )}
                <div className="upload-area">
                  {uploadedFiles.length > 0 && (
                    <div className="upload-list">
                      {uploadedFiles.map((f) => (
                        <div className="upload-item-row" key={f.uid}>
                          <div className={`file-icon ${f.ext === "pdf" ? "is-pdf" : ""}`}>
                            {f.ext === "pdf" ? <FilePdfOutlined /> : <FileImageOutlined />}
                          </div>
                          <div className="file-info">
                            <div className="file-name">{f.name}</div>
                            <div className="file-meta">
                              {formatSize(f.size)}
                              {f.status === "uploading" && <span> · Subiendo...</span>}
                              {f.status === "failed" && <span className="failed"> · Error</span>}
                            </div>
                          </div>
                          <div className="file-status">
                            {f.status === "done" && <CheckCircleFilled className="status-done" />}
                            {f.status === "uploading" && <LoadingOutlined className="file-spin" />}
                          </div>
                          <button
                            type="button"
                            className="remove-btn"
                            onClick={() => removeFile(f.uid)}
                            aria-label="remove"
                          >
                            <CloseOutlined />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {uploadedFiles.length < MAX_FILES && (
                    <Dragger
                      name="file"
                      multiple
                      showUploadList={false}
                      accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                      beforeUpload={beforeUpload}
                      customRequest={customRequest}
                      className="upload-dragger"
                    >
                      <p className="upload-icon">
                        <InboxOutlined />
                      </p>
                      <p className="upload-title">
                        {uploadedFiles.length === 0
                          ? "Haga clic o arrastre archivos aquí"
                          : `Agregar más archivos (${uploadedFiles.length}/${MAX_FILES})`}
                      </p>
                      <p className="upload-tip">
                        Máximo 5 archivos (PDF, PNG, JPG). Hasta 3MB cada uno.
                      </p>
                    </Dragger>
                  )}
                </div>
              </Form.Item>

              <Form.Item
                name="agree"
                valuePropName="checked"
                className="agree-item"
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error("Debe aceptar la declaración para continuar.")
                          ),
                  },
                ]}
              >
                <Checkbox>
                  Declaro que la información proporcionada es verídica y acepto los términos de servicio.
                </Checkbox>
              </Form.Item>

              <Form.Item className="submit-item">
                <Button
                  type="primary"
                  className="submit-btn"
                  loading={submitting}
                  onClick={handleSubmit}
                  block
                >
                  Enviar Solicitud
                </Button>
              </Form.Item>
            </Form>
          </div>

        <div className="legal-footer">
          <div className="legal-icon">
            <SafetyCertificateOutlined />
          </div>
          <p>
            Si desea realizar su reclamo ante la autoridad competente, puede hacerlo a través del sitio web de Defensa del Consumidor.{" "}
            <a
              href="https://autogestion.produccion.gob.ar/consumidores"
              target="_blank"
              rel="noopener noreferrer"
              className="legal-link"
            >
              Haga clic aquí
            </a>
            .
          </p>
        </div>
      </div>

      <Modal
        open={showSuccess}
        centered
        width={560}
        footer={null}
        onCancel={backToForm}
        className="success-modal"
      >
        <div className="success-card">
          <div className="success-icon">
            <CheckCircleFilled />
          </div>
          <h2 className="success-title">Hemos recibido su presentación correctamente.</h2>
          <p className="success-text">
            Le enviaremos una confirmación de recepción por correo electrónico y nuestro equipo
            analizará su caso. Le responderemos dentro de los plazos legales aplicables.
          </p>
          <Button type="primary" className="success-btn" onClick={backToForm}>
            Cerrar
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default QuejasyReclamos;
